import { i18n } from "@next-core/i18n";
import { replaceUseChildren } from "@next-core/utils/storyboard";
import { uniqueId } from "lodash";
import { getDataStores, postAsyncRender, renderBricks } from "./internal/Renderer.js";
import { RendererContext } from "./internal/RendererContext.js";
import { DataStore } from "./internal/data/DataStore.js";
import { mountTree, unmountTree } from "./internal/mount.js";
import { applyMode, applyTheme, setMode, setTheme } from "./themeAndMode.js";
import { RenderTag } from "./internal/enums.js";
import { registerStoryboardFunctions } from "./internal/compute/StoryboardFunctions.js";
import { registerAppI18n } from "./internal/registerAppI18n.js";
import { registerCustomTemplates } from "./internal/registerCustomTemplates.js";
import { setUIVersion } from "./setUIVersion.js";
import { ErrorNode } from "./internal/ErrorNode.js";
import { isolatedFunctionRegistry, registerIsolatedFunctions } from "./internal/compute/IsolatedFunctions.js";
import { isolatedTemplateRegistryMap, registerIsolatedTemplates } from "./internal/IsolatedTemplates.js";
import { hooks } from "./internal/Runtime.js";
export function unstable_createRoot(container) {
  let {
    portal: _portal,
    scope = "fragment",
    unknownBricks,
    supportsUseChildren,
    unsafe_penetrate
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let portal = _portal;
  let createPortal;
  if (_portal) {
    createPortal = _portal;
  } else {
    // Create portal container when necessary.
    createPortal = () => {
      portal = document.createElement("div");
      portal.style.position = "absolute";
      portal.style.width = portal.style.height = "0";
      document.body.append(portal);
      return portal;
    };
  }
  let unmounted = false;
  let rendererContext;
  let clearI18nBundles;
  const isolatedRoot = scope === "page" ? undefined : Symbol("IsolatedRoot");
  return {
    async render(brick) {
      var _hooks$auth;
      let {
        theme,
        uiVersion,
        language,
        context,
        functions,
        templates,
        i18n: i18nData,
        url,
        app
      } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      if (unmounted) {
        throw new Error("The root is unmounted and cannot be rendered any more");
      }
      const bricks = [].concat(brick);
      if (supportsUseChildren) {
        replaceUseChildren(bricks);
        for (const template of templates !== null && templates !== void 0 ? templates : []) {
          if (Array.isArray(template.bricks)) {
            replaceUseChildren(template.bricks);
          }
        }
      }
      const previousRendererContext = rendererContext;
      const renderId = uniqueId("render-id-");
      rendererContext = new RendererContext(scope, {
        unknownBricks,
        renderId
      });
      const runtimeContext = {
        ctxStore: new DataStore("CTX", undefined, rendererContext),
        pendingPermissionsPreCheck: [],
        tplStateStoreMap: new Map(),
        formStateStoreMap: new Map(),
        isolatedRoot,
        unsafe_penetrate,
        sys: {
          ...(hooks === null || hooks === void 0 || (_hooks$auth = hooks.auth) === null || _hooks$auth === void 0 ? void 0 : _hooks$auth.getAuth())
        }
      };
      if (url) {
        const urlObj = new URL(url);
        runtimeContext.query = urlObj.searchParams;
        runtimeContext.location = {
          pathname: urlObj.pathname,
          search: urlObj.search,
          hash: urlObj.hash,
          state: undefined
        };
      }
      const renderRoot = {
        tag: RenderTag.ROOT,
        container,
        createPortal
      };
      if (scope === "page") {
        var _clearI18nBundles;
        setTheme(theme !== null && theme !== void 0 ? theme : "light");
        setMode("default");
        setUIVersion(uiVersion);
        if (language) {
          await i18n.changeLanguage(language);
        }
        app !== null && app !== void 0 ? app : app = {
          id: "demo",
          homepage: "/demo"
        };
        runtimeContext.app = app;
        const demoStoryboard = {
          app,
          meta: {
            i18n: i18nData,
            customTemplates: templates
          }
        };

        // Register i18n.
        (_clearI18nBundles = clearI18nBundles) === null || _clearI18nBundles === void 0 || _clearI18nBundles();
        clearI18nBundles = registerAppI18n(demoStoryboard);

        // Register custom templates.
        registerCustomTemplates(demoStoryboard);

        // Register functions.
        registerStoryboardFunctions(functions, app);
      } else {
        registerIsolatedTemplates(isolatedRoot, templates);
        registerIsolatedFunctions(isolatedRoot, functions);
      }
      runtimeContext.ctxStore.define(context, runtimeContext);
      let failed = false;
      let output;
      let stores = [];
      try {
        output = await renderBricks(renderRoot, bricks, runtimeContext, rendererContext, [], {});
        stores = getDataStores(runtimeContext);
        await postAsyncRender(output, runtimeContext, stores);
      } catch (error) {
        failed = true;
        output = {
          node: await ErrorNode(error, renderRoot, scope === "page"),
          blockingList: []
        };
      }
      renderRoot.child = output.node;
      previousRendererContext === null || previousRendererContext === void 0 || previousRendererContext.dispatchOnUnmount();
      previousRendererContext === null || previousRendererContext === void 0 || previousRendererContext.dispose();
      unmountTree(container);
      if (portal) {
        unmountTree(portal);
      }
      if (scope === "page") {
        if (!failed) {
          rendererContext.dispatchBeforePageLoad();
        }
        applyTheme();
        applyMode();
      }
      mountTree(renderRoot);
      if (scope === "page") {
        window.scrollTo(0, 0);
      }
      if (!failed) {
        for (const store of stores) {
          store.mountAsyncData();
        }
        if (scope === "page") {
          rendererContext.dispatchPageLoad();
          // rendererContext.dispatchAnchorLoad();
        }
        rendererContext.dispatchOnMount();
        rendererContext.initializeScrollIntoView();
        rendererContext.initializeMediaChange();
        rendererContext.initializeMessageDispatcher();
      }
    },
    unmount() {
      if (unmounted) {
        return;
      }
      unmounted = true;
      if (isolatedRoot) {
        isolatedFunctionRegistry.delete(isolatedRoot);
        isolatedTemplateRegistryMap.delete(isolatedRoot);
      }
      unmountTree(container);
      if (portal) {
        unmountTree(portal);
        // Only remove the portal from its parent when it's dynamic created.
        if (!_portal) {
          portal.remove();
        }
      }
    }
  };
}
//# sourceMappingURL=createRoot.js.map